import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const ServicesGrid = () => {
  const services = [
    {
      id: 1,
      title: "Cloud Solutions",
      description: "Scalable infrastructure and migration services",
      icon: "Cloud",
      features: ["AWS/Azure Migration", "Serverless Architecture", "Auto-scaling Solutions"],
      color: "from-blue-500/10 to-cyan-500/10",
      borderColor: "border-blue-500/20",
      iconColor: "text-blue-400"
    },
    {
      id: 2,
      title: "Cybersecurity",
      description: "Advanced threat protection and compliance",
      icon: "Shield",
      features: ["Threat Detection", "Security Audits", "Compliance Management"],
      color: "from-red-500/10 to-orange-500/10",
      borderColor: "border-red-500/20",
      iconColor: "text-red-400"
    },
    {
      id: 3,
      title: "Full-Stack Development",
      description: "End-to-end web and mobile applications",
      icon: "Code",
      features: ["React/Node.js", "Mobile Apps", "API Development"],
      color: "from-green-500/10 to-emerald-500/10",
      borderColor: "border-green-500/20",
      iconColor: "text-green-400"
    },
    {
      id: 4,
      title: "UI/UX Design",
      description: "User-centered design and experiences",
      icon: "Palette",
      features: ["User Research", "Prototyping", "Design Systems"],
      color: "from-purple-500/10 to-pink-500/10",
      borderColor: "border-purple-500/20",
      iconColor: "text-purple-400"
    },
    {
      id: 5,
      title: "Data Analytics",
      description: "Business intelligence and insights",
      icon: "BarChart3",
      features: ["Machine Learning", "Data Visualization", "Predictive Analytics"],
      color: "from-yellow-500/10 to-amber-500/10",
      borderColor: "border-yellow-500/20",
      iconColor: "text-yellow-400"
    },
    {
      id: 6,
      title: "Managed IT Support",
      description: "24/7 monitoring and maintenance",
      icon: "Settings",
      features: ["System Monitoring", "Help Desk", "Infrastructure Management"],
      color: "from-indigo-500/10 to-blue-500/10",
      borderColor: "border-indigo-500/20",
      iconColor: "text-indigo-400"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="py-20 bg-muted/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Why Choose <span className="text-accent">DARAJA.LABS</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            We deliver modern solutions that bridge the gap between your current state and digital excellence
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {services?.map((service) => (
            <motion.div
              key={service?.id}
              variants={cardVariants}
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.2 }
              }}
              className="group cursor-pointer"
            >
              <Link to="/services">
                <div className={`relative bg-gradient-to-br ${service?.color} backdrop-blur-sm border ${service?.borderColor} rounded-2xl p-8 h-full hover:shadow-2xl hover:shadow-accent/10 transition-all duration-300 overflow-hidden`}>
                  {/* Glow Effect */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-accent/0 via-accent/5 to-accent/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {/* Animated Border */}
                  <div className="absolute inset-0 rounded-2xl border border-accent/0 group-hover:border-accent/30 transition-colors duration-300" />
                  
                  <div className="relative z-10">
                    {/* Icon */}
                    <div className="mb-6">
                      <div className={`w-16 h-16 bg-card/50 rounded-xl flex items-center justify-center group-hover:bg-accent/20 transition-colors duration-300`}>
                        <Icon name={service?.icon} size={32} className={`${service?.iconColor} group-hover:text-accent transition-colors duration-300`} />
                      </div>
                    </div>

                    {/* Content */}
                    <h3 className="text-xl font-bold text-foreground mb-3 group-hover:text-accent transition-colors duration-300">
                      {service?.title}
                    </h3>
                    
                    <p className="text-text-secondary mb-6 leading-relaxed">
                      {service?.description}
                    </p>

                    {/* Features */}
                    <ul className="space-y-2 mb-6">
                      {service?.features?.map((feature, index) => (
                        <li key={index} className="flex items-center text-sm text-text-secondary">
                          <Icon name="Check" size={16} className="text-accent mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {/* CTA */}
                    <div className="flex items-center text-accent group-hover:text-accent/80 font-semibold transition-colors duration-200">
                      Learn More
                      <Icon 
                        name="ArrowRight" 
                        size={16} 
                        className="ml-2 group-hover:translate-x-1 transition-transform duration-200" 
                      />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Section */}
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <Link to="/application">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-accent text-accent-foreground px-8 py-4 rounded-lg font-semibold text-lg hover:bg-accent/90 transition-colors duration-200 shadow-lg hover:shadow-accent/20"
            >
              Start Your Project
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesGrid;